/**
 * MentorCard Component
 *
 * Displays a single mentor as a card in the mentor directory. Shows the
 * mentor's avatar (or initials fallback), name, bio, areas of expertise,
 * preferred audience, availability types, affiliated organizations, and
 * external links (LinkedIn, website).
 *
 * Each badge section is conditionally rendered based on available data,
 * so cards gracefully adapt to mentors with sparse profiles. Organization
 * badges link to the corresponding organization detail page.
 *
 * Used by the MentorDirectory component on the Discover page.
 */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Card, Badge } from 'react-bootstrap';

import { getAvailableForLabel } from '@/shared/constants/mentorConstants';
import { normalizeUrl } from '@/shared/utils/normalizeUrl';

MentorCard.propTypes = {
    mentor: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        name: PropTypes.shape({
            first: PropTypes.string,
            last: PropTypes.string,
        }),
        profilePicture: PropTypes.string,
        bio: PropTypes.string,
        mentorProfile: PropTypes.shape({
            areasOfExpertise: PropTypes.arrayOf(PropTypes.string),
            industryExperience: PropTypes.arrayOf(PropTypes.string),
            availableFor: PropTypes.arrayOf(PropTypes.string),
            preferredAudience: PropTypes.arrayOf(PropTypes.string),
            mentorBio: PropTypes.string,
            linkedInUrl: PropTypes.string,
            website: PropTypes.string,
            contactPhone: PropTypes.string,
            contactEmail: PropTypes.string,
        }),
        affiliations: PropTypes.arrayOf(
            PropTypes.shape({
                organization: PropTypes.shape({
                    _id: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.object,
                    ]),
                    name: PropTypes.string,
                    primarySector: PropTypes.string,
                }),
                type: PropTypes.string,
            })
        ),
    }).isRequired,
    // Called with an expertise value when a chip is clicked; lets the parent
    // directory filter to that expertise. Optional -- chips are inert without it.
    onExpertiseClick: PropTypes.func,
};

export default function MentorCard({ mentor, onExpertiseClick }) {
    const [imgError, setImgError] = useState(false);
    const [showFullBio, setShowFullBio] = useState(false);
    const [isClamped, setIsClamped] = useState(false);
    const bioRef = useRef(null);

    useEffect(() => {
        const el = bioRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => {
            setIsClamped(el.scrollHeight > el.clientHeight);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    // Derive display values with safe defaults for missing data
    const name = mentor.name || {};
    const fullName = `${name.first || ''} ${name.last || ''}`.trim();
    // Build initials from whichever name parts exist. Indexing an empty
    // string yields `undefined`, so guard each part before taking its first
    // character, then fall back to a single '?' when neither part is present
    // (otherwise a missing first name rendered the literal "?undefined").
    const initials =
        `${name.first ? name.first[0] : ''}${name.last ? name.last[0] : ''}` ||
        '?';
    const profile = mentor.mentorProfile || {};
    const affiliations = mentor.affiliations || [];
    // Expertise areas double as quick-tag chips (the mentor equivalent of an
    // org's sectors). Show at most 2, with the rest behind a "+N more" label.
    const expertise = profile.areasOfExpertise || [];
    const visibleExpertise = expertise.slice(0, 2);
    const hiddenExpertiseCount = expertise.length - visibleExpertise.length;
    return (
        <Card className="h-100 border-0 shadow-sm mentor-card">
            <Card.Body className="p-4 d-flex flex-column">
                {/* Header: avatar + name */}
                <div className="d-flex align-items-center mb-3">
                    {mentor.profilePicture && !imgError ? (
                        <img
                            src={mentor.profilePicture}
                            alt={fullName}
                            className="rounded-circle mentor-avatar me-3"
                            onError={() => setImgError(true)}
                        />
                    ) : (
                        <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center mentor-avatar-initials me-3 text-white fw-bold">
                            {initials}
                        </div>
                    )}
                    <div>
                        <h6 className="mb-0 text-primary">{fullName}</h6>
                        {profile.availability && (
                            <small className="text-muted">
                                {profile.availability}
                            </small>
                        )}
                    </div>
                </div>

                {/* Mentor bio */}
                {profile.mentorBio && (
                    <>
                        <p
                            ref={bioRef}
                            className={`small text-muted mb-1 ${showFullBio ? '' : 'mentor-bio-text'}`}
                        >
                            {profile.mentorBio}
                        </p>
                        {(isClamped || showFullBio) && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-primary p-0 mb-3"
                                onClick={() => setShowFullBio((prev) => !prev)}
                            >
                                {showFullBio ? 'Show less' : 'Read more'}
                            </button>
                        )}
                    </>
                )}

                {/* Areas of expertise -- shown as clickable quick-tag chips */}
                {expertise.length > 0 && (
                    <div className="mb-2">
                        <small className="text-muted d-block mb-1">
                            Expertise
                        </small>
                        <div className="tag-chips">
                            {visibleExpertise.map((area) => (
                                <button
                                    key={area}
                                    type="button"
                                    className="tag-chip"
                                    aria-label={`Filter mentors by ${area}`}
                                    onClick={() => onExpertiseClick?.(area)}
                                >
                                    #{area}
                                </button>
                            ))}
                            {hiddenExpertiseCount > 0 && (
                                <span className="tag-chip-more">
                                    +{hiddenExpertiseCount} more
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Preferred audience */}
                {profile.preferredAudience?.length > 0 && (
                    <div className="mb-2">
                        <small className="text-muted d-block mb-1">
                            Works with
                        </small>
                        <div className="d-flex flex-wrap gap-1">
                            {profile.preferredAudience.map((aud) => (
                                <Badge
                                    bg="light"
                                    text="dark"
                                    key={aud}
                                    className="fw-normal"
                                >
                                    {aud}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}

                {/* Available for */}
                {profile.availableFor?.length > 0 && (
                    <div className="mb-2">
                        <small className="text-muted d-block mb-1">
                            Available for
                        </small>
                        <div className="d-flex flex-wrap gap-1">
                            {profile.availableFor.map((item) => (
                                <Badge
                                    bg="outline-primary"
                                    key={item}
                                    className="fw-normal border text-primary"
                                >
                                    {getAvailableForLabel(item)}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}

                {/* Affiliated organizations */}
                {affiliations.length > 0 && (
                    <div className="mb-2 mt-auto">
                        <small className="text-muted d-block mb-1">
                            Connected to
                        </small>
                        <div className="d-flex flex-wrap gap-1">
                            {affiliations.map((aff) => (
                                <Link
                                    key={
                                        aff.organization?._id?.toString() ||
                                        aff.organization?.name
                                    }
                                    to={`/organizations/${aff.organization?._id}`}
                                    className="text-decoration-none"
                                >
                                    <Badge
                                        bg="light"
                                        text="dark"
                                        className="fw-normal"
                                    >
                                        {aff.organization?.name}
                                    </Badge>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {/* External links */}
                {(profile.linkedInUrl || profile.website) && (
                    <div className="d-flex gap-3 mt-auto pt-2 border-top">
                        {profile.linkedInUrl && (
                            <a
                                href={normalizeUrl(profile.linkedInUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="small text-primary text-decoration-none"
                            >
                                LinkedIn
                            </a>
                        )}
                        {profile.website && (
                            <a
                                href={normalizeUrl(profile.website)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="small text-primary text-decoration-none"
                            >
                                Website
                            </a>
                        )}
                    </div>
                )}

                {/* Contact info */}
                {(profile.contactPhone || profile.contactEmail) && (
                    <div className="d-flex gap-3 mt-2">
                        {profile.contactPhone && (
                            <a
                                href={`tel:${profile.contactPhone}`}
                                className="small text-primary text-decoration-none"
                            >
                                {profile.contactPhone}
                            </a>
                        )}
                        {profile.contactEmail && (
                            <a
                                href={`mailto:${profile.contactEmail}`}
                                className="small text-primary text-decoration-none"
                            >
                                {profile.contactEmail}
                            </a>
                        )}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}
